/**
 * React bindings for the session.
 *
 * The manager is a plain module with a subscribe function rather than a context that holds state,
 * because the API client needs the access token and the refresh path *outside* React — a query fired
 * from a router loader, or from an event handler, must not have to go through a hook. This file is the
 * thin adapter: `useSyncExternalStore` is the supported way to read an external store from a
 * component, and it is what makes the state consistent during a concurrent render.
 */

/// <reference types="vite/client" />
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import type { AuthUser } from "@trippool/contracts";

import { createSessionManager, type PublicMode, type SessionManager, type SessionState } from "./session.ts";

const SessionContext = createContext<SessionManager | null>(null);

export interface SessionProviderProps {
  children: ReactNode;
  /** Injected by a test; production builds the real one. */
  manager?: SessionManager;
}

export function SessionProvider({ children, manager }: SessionProviderProps): ReactNode {
  // Created once. Rebuilding the manager on a render would drop the in-memory token and silently sign
  // the user out on any state change that re-rendered this provider.
  const managerRef = useRef<SessionManager | null>(manager ?? null);
  managerRef.current ??= createSessionManager();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            /*
             * A plan's data and the user's own places change only when the user changes them, so
             * refetching on every window focus is traffic for nothing. The places that do change
             * underneath — a session list — opt in individually.
             */
            refetchOnWindowFocus: false,
            /*
             * Retrying a 4xx is pointless: the request was wrong and will be wrong again. Retrying a
             * network failure or a 503 is worth it, and the client already does that internally for
             * the requests it makes — this covers a failure that exhausted that.
             */
            retry: (failureCount, error) => {
              const status = (error as { status?: number }).status;
              if (typeof status === "number" && status >= 400 && status < 500) return false;
              return failureCount < 2;
            },
            staleTime: 30_000,
          },
        },
      }),
    [],
  );

  return (
    <SessionContext.Provider value={managerRef.current}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </SessionContext.Provider>
  );
}

export function useSessionManager(): SessionManager {
  const manager = useContext(SessionContext);
  if (manager === null) {
    // A programming error, not a runtime condition: a screen rendered outside the provider has no
    // session and no query client, so failing loudly here beats every request returning 401 later.
    throw new Error("useSessionManager must be used inside a SessionProvider");
  }
  return manager;
}

/** The session state, subscribed. */
export function useSessionState(): SessionState {
  const manager = useSessionManager();
  return useSyncExternalStore(
    (listener) => manager.subscribe(listener),
    () => manager.getState(),
  );
}

/** The signed-in account, or null. */
export function useCurrentUser(): AuthUser | null {
  return useSessionState().user;
}

/**
 * What this deployment is: `unknown` until `/meta` answers, then `public` or `private`.
 *
 * A hook rather than two booleans, because the answer arrives with the boot and the phase before it
 * matters: a screen that read "not public" out of an unanswered `/meta` was calling the deployment
 * private, which is how a hard load of a non-home path came to paint an authentication form on a
 * deployment that has no sign-in at all. Subscribed through the state, so the re-render and the answer
 * are the same event.
 */
export function usePublicMode(): PublicMode {
  return useSessionState().mode;
}

/**
 * Restore the session once, on mount.
 *
 * Returns whether the app has finished asking. Until it has, a screen must not decide the user is
 * signed out — the access token is deliberately absent after a reload, so "no user" before this
 * resolves means "not known yet".
 *
 * `true` also means the mode has been decided, which is what lets the shell treat this one flag as its
 * permission to render anything: the boot publishes the mode before anything else, and an unreadable
 * `/meta` ends it without settling rather than guessing `private`.
 */
export function useRestoredSession(): boolean {
  const manager = useSessionManager();
  const state = useSessionState();
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (requested) return;
    setRequested(true);
    void manager.restore();
  }, [manager, requested]);

  return state.restored;
}
